import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ShieldCheck, ShieldAlert } from 'lucide-react';
import { notify } from '../../components/Notify';
import AuthShell from '../../components/AuthShell';
import { PasswordField } from '../../components/Fields';
import { validatePasswordResetTokenApi, confirmPasswordResetApi } from '../../api';

const MSG_TOKEN = {
    expirado: 'Este link expirou. Peça um novo para redefinir a senha.',
    usado: 'Este link já foi usado. Peça um novo se precisar.',
    invalido: 'Link inválido. Peça um novo para redefinir a senha.',
};

export default function RedefinirSenha() {
    const [params] = useSearchParams();
    const token = params.get('token') || '';
    const navigate = useNavigate();

    const [estado, setEstado] = useState('checking'); // checking|ok|invalid|success
    const [motivo, setMotivo] = useState('invalido');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [erros, setErros] = useState({});
    const [enviando, setEnviando] = useState(false);

    useEffect(() => {
        let vivo = true;
        if (!token) { setEstado('invalid'); setMotivo('invalido'); return; }
        validatePasswordResetTokenApi({ token })
            .then((res) => {
                if (!vivo) return;
                if (res?.data?.valid) setEstado('ok');
                else { setMotivo(res?.data?.error || 'invalido'); setEstado('invalid'); }
            })
            .catch((err) => {
                if (!vivo) return;
                setMotivo(err?.response?.data?.error || 'invalido');
                setEstado('invalid');
            });
        return () => { vivo = false; };
    }, [token]);

    const salvar = async (ev) => {
        ev.preventDefault();
        const e = {};
        if (!password || password.length < 6) e.password = 'Mínimo de 6 caracteres.';
        if (confirm !== password) e.confirm = 'As senhas não coincidem.';
        setErros(e);
        if (Object.keys(e).length) return;

        setEnviando(true);
        try {
            await confirmPasswordResetApi({ token, password });
            setEstado('success');
            notify('Senha redefinida com sucesso!');
            setTimeout(() => navigate('/login'), 2500);
        } catch (err) {
            const msg = err?.response?.data?.error || '';
            if (/expir/i.test(msg)) { setMotivo('expirado'); setEstado('invalid'); }
            else if (/usad/i.test(msg)) { setMotivo('usado'); setEstado('invalid'); }
            else if (/inv[aá]lid/i.test(msg)) { setMotivo('invalido'); setEstado('invalid'); }
            else notify(msg || 'Não foi possível redefinir a senha.');
        } finally {
            setEnviando(false);
        }
    };

    if (estado === 'checking') {
        return <AuthShell><div className="ueb-auth-card pcenter"><span className="pspin" style={{ color: 'var(--p-accent)' }} /><p className="ueb-auth-sub" style={{ marginTop: 14 }}>Validando o link…</p></div></AuthShell>;
    }

    if (estado === 'invalid') {
        return (
            <AuthShell>
                <div className="ueb-auth-card pcenter">
                    <div className="ueb-auth-ico is-danger"><ShieldAlert size={40} /></div>
                    <h1>Link indisponível</h1>
                    <p className="ueb-auth-sub">{MSG_TOKEN[motivo] || MSG_TOKEN.invalido}</p>
                    <Link className="pbtn pbtn--primary pbtn--block" to="/esqueci-senha">Pedir novo link</Link>
                    <p className="ueb-auth-foot"><Link className="plink" to="/login">Voltar ao login</Link></p>
                </div>
            </AuthShell>
        );
    }

    if (estado === 'success') {
        return (
            <AuthShell>
                <div className="ueb-auth-card pcenter">
                    <div className="ueb-auth-ico is-success"><ShieldCheck size={40} /></div>
                    <h1>Senha redefinida</h1>
                    <p className="ueb-auth-sub">Tudo certo! Redirecionando para o login…</p>
                    <Link className="pbtn pbtn--primary pbtn--block" to="/login">Ir para o login</Link>
                </div>
            </AuthShell>
        );
    }

    return (
        <AuthShell>
            <form className="ueb-auth-card" onSubmit={salvar}>
                <h1>Criar nova senha</h1>
                <p className="ueb-auth-sub">Escolha uma nova senha para sua conta.</p>

                <PasswordField
                    id="password"
                    label="Nova senha"
                    autoComplete="new-password"
                    placeholder="Mínimo de 6 caracteres"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    error={erros.password}
                />
                <PasswordField
                    id="confirm"
                    label="Confirmar senha"
                    autoComplete="new-password"
                    placeholder="Repita a senha"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    error={erros.confirm}
                />

                <button className="pbtn pbtn--primary pbtn--block pbtn--lg" type="submit" disabled={enviando}>
                    {enviando ? <span className="pspin" /> : 'Salvar nova senha'}
                </button>
            </form>
        </AuthShell>
    );
}
